import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMerchantStore } from "../store/useMerchantStore";
import { api } from "../services/api";
import { EmptyState, FilterBar, PageHeader, SearchField } from "./orderKit";
import { PaymentChip, RouteCard, RouteStats, openRouteDetails, parseNumber, routeMatchesSearch } from "./routeKit";

import { t } from "../i18n";
const SORTS = {
  newest: { label: "routes.sort.newest", fn: (a, b) => Number(b.routeId) - Number(a.routeId) },
  stops: { label: "routes.sort.most_stops", fn: (a, b) => (b.orderedOrderIds?.length || 0) - (a.orderedOrderIds?.length || 0) },
  distance: {
    label: "routes.sort.longest",
    fn: (a, b) => (parseNumber(b.totalDistanceKm) || 0) - (parseNumber(a.totalDistanceKm) || 0),
  },
};

function isPaid(route) {
  const s = String(route.paymentStatus || "").toUpperCase();
  return s === "COMPLETED" || s === "PAID" || s === "SUCCESS";
}

/**
 * Shared list page for Planned (DRAFT) and Confirmed routes.
 * `paymentFilter` adds All / Fee paid / Fee unpaid chips (confirmed routes pay the ODOS fee before pickup).
 */
export function RouteListPage({ status, eyebrow, title, lead, detailPath, emptyTitle, emptyMessage, paymentFilter }) {
  const { merchantId } = useMerchantStore();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openingId, setOpeningId] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/api/orders/routes`, { params: { merchantId, status } });
      setRoutes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || t("routes.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [merchantId, status, title]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpen = async (route) => {
    setOpeningId(route.routeId);
    setError("");
    try {
      await openRouteDetails({ route, merchantId, navigate, path: detailPath });
    } catch (err) {
      console.error("Failed to open route:", err);
      setError(t("routes.open_failed"));
      setOpeningId(null);
    }
  };

  const filterOptions = useMemo(() => {
    const paid = routes.filter(isPaid).length;
    return [
      { id: "all", label: t("common.all"), count: routes.length },
      { id: "paid", label: t("route.fee_paid"), count: paid, dot: "#2f7a57" },
      { id: "unpaid", label: t("route.fee_unpaid"), count: routes.length - paid, dot: "#8a6420" },
    ];
  }, [routes]);

  const visible = useMemo(() => {
    const list = routes.filter((r) => {
      if (paymentFilter && filter === "paid" && !isPaid(r)) return false;
      if (paymentFilter && filter === "unpaid" && isPaid(r)) return false;
      return routeMatchesSearch(r, search);
    });
    return [...list].sort(SORTS[sort].fn);
  }, [routes, filter, search, sort, paymentFilter]);

  if (!merchantId) {
    return <div className="card">{t("routes.sign_in")}</div>;
  }

  return (
    <div className="page-container">
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        lead={lead}
        actions={
          <button type="button" onClick={load} disabled={loading}>
            <i className={`fa-solid fa-rotate-right${loading ? " fa-spin" : ""}`} aria-hidden="true" /> {t("common.refresh")}
          </button>
        }
      />

      {error ? (
        <div className="alert alert--error">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
        </div>
      ) : null}

      {loading && routes.length === 0 ? (
        <div className="loading">{t("routes.loading")}</div>
      ) : routes.length === 0 ? (
        <EmptyState icon="fa-route" title={emptyTitle} message={emptyMessage} />
      ) : (
        <>
          <RouteStats routes={routes} payLabel={t("routes.route_earnings")} />

          <div className="list-toolbar">
            {paymentFilter ? (
              <FilterBar options={filterOptions} value={filter} onChange={setFilter} ariaLabel={t("routes.payment")} />
            ) : (
              <div />
            )}
            <div className="list-toolbar__right">
              <select className="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label={t("routes.sort_aria")}>
                {Object.entries(SORTS).map(([id, s]) => (
                  <option key={id} value={id}>
                    {t(s.label)}
                  </option>
                ))}
              </select>
              <SearchField
                value={search}
                onChange={setSearch}
                placeholder={t("routes.search_placeholder")}
                ariaLabel={t("routes.search_aria")}
              />
            </div>
          </div>

          {visible.length === 0 ? (
            <EmptyState
              icon="fa-route"
              title={t("common.nothing_here")}
              message={t("routes.no_match")}
              action={
                <button
                  type="button"
                  onClick={() => {
                    setFilter("all");
                    setSearch("");
                  }}
                >
                  {t("routes.show_all")}
                </button>
              }
            />
          ) : (
            <div className="route-cards">
              {visible.map((route) => (
                <RouteCard
                  key={route.routeId}
                  route={route}
                  onOpen={handleOpen}
                  busy={openingId === route.routeId}
                  extraChips={paymentFilter ? <PaymentChip status={route.paymentStatus} /> : null}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
